import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useAssistant } from "@/context/AssistantContext";
import { useApi } from "@/hooks/use-api";
import { ErrorState } from "@/components/common/EmptyState";
import { Delta } from "@/components/common/Delta";
import { DailyEnergyChart } from "@/components/charts/SimpleCharts";
import { TodayPowerChart } from "@/components/charts/TodayPowerChart";
import { PlantTable, plantShortName, sortDeviationsFirst } from "@/components/plants/PlantTable";
import { LensMark } from "@/components/brand/ProximaLogo";
import { fmtMWh, fmtNOK, fmtNum, fmtPrice, fmtTime } from "@/lib/format";
import { STATUS } from "@/lib/status";
import { cn } from "@/lib/utils";

/* The overview is organised around production: the customer's name as the page title, the measured power in the
   chart's own head, a tall chart, and «Krever oppfølging» beside it. Supporting numbers come after – quietly. */

/* ---------- Krever oppfølging: concrete deviations, most severe first ---------- */
const FOLLOW_UP_LIMIT = 6;

const FollowUp = ({ plants = [], events = [], loading, asOf }) => {
  const items = useMemo(() => {
    const latestEvent = {};
    events.forEach((e) => {
      if (e.status !== "resolved" && !latestEvent[e.plant_id]) latestEvent[e.plant_id] = e;
    });
    return sortDeviationsFirst(plants.filter((p) => p.status !== "running" || (p.events_open || 0) > 0)).map((p) => ({ p, e: latestEvent[p.id] }));
  }, [plants, events]);

  if (loading)
    return (
      <div className="space-y-4 pt-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
    );
  if (!items.length) return <p className="pt-3 text-[14.5px] leading-relaxed text-muted-foreground">Ingen avvik akkurat nå. Alle kraftverk produserer normalt, og det er ingen aktive hendelser.</p>;

  const shown = items.slice(0, FOLLOW_UP_LIMIT);
  const rest = items.length - shown.length;
  return (
    <>
      <ul className="divide-y divide-border" data-testid="follow-up-list">
        {shown.map(({ p, e }) => {
          const deviation = p.status !== "running";
          /* a plant that runs again while its event is still open: say so, instead of repeating the event's label as if it were current */
          const restored = !deviation && e && e.ended_at;
          const word = deviation ? STATUS[p.status]?.label : restored ? "I drift igjen" : "Hendelse";
          const tone = p.status === "stopped" || (!deviation && p.events_critical && !restored) ? "text-rose-700 dark:text-rose-300" : deviation ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground";
          const reason = p.status_reason || e?.title;
          const ack = e ? (e.status === "open" ? "ikke kvittert" : e.status === "acknowledged" ? "kvittert" : null) : null;
          const meta = [
            restored ? `Avsluttet kl. ${fmtTime(e.ended_at)}` : e ? `Siden kl. ${fmtTime(e.started_at)}` : `Målt kl. ${fmtTime(asOf)}`,
            ack,
            deviation ? `${fmtNum(p.power_mw, p.power_mw < 1 ? 2 : 1)} av ${fmtNum(p.capacity_mw, 1)} MW` : null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <li key={p.id} className="py-3 first:pt-0" data-testid={`follow-up-${p.id}`}>
              <div className="flex items-baseline justify-between gap-3">
                <Link to={`/kraftverk/${p.id}`} className="truncate text-[15px] font-semibold leading-snug text-foreground hover:text-primary" data-testid={`follow-up-plant-${p.id}`}>
                  {plantShortName(p.name)}
                </Link>
                <span className={cn("shrink-0 text-[13px] font-medium", tone)}>{word}</span>
              </div>
              {reason ? <div className="mt-0.5 text-[14px] leading-snug text-foreground">{reason}</div> : null}
              <div className="metric mt-0.5 text-[13px] leading-snug text-muted-foreground">{meta}</div>
              {e ? (
                <Link to={`/hendelser?id=${e.id}`} className="mt-1.5 inline-block text-[13px] font-medium text-primary hover:underline" data-testid={`follow-up-event-${p.id}`}>
                  Se hendelsen →
                </Link>
              ) : null}
            </li>
          );
        })}
      </ul>
      {rest > 0 ? (
        <Link to="/hendelser" className="mt-1 inline-block text-[13.5px] font-medium text-primary hover:underline" data-testid="follow-up-more-link">
          {rest} til i hendelseslisten
        </Link>
      ) : null}
    </>
  );
};

/* ---------- Page ---------- */
const RANGE = {
  today: { tab: "I dag", ask: "Forklar utviklingen i målt effekt for porteføljen i dag sammenlignet med i går. Hent timeverdier for i dag og i går, vannføring og aktive hendelser, og skill tydelig mellom det som er dokumentert i dataene og mulige forklaringer.", label: "I dag mot i går" },
  "7d": { tab: "7 dager", ask: "Forklar endringen i produksjonen for porteføljen siste 7 dager. Hent produksjon per døgn, vannføring og hendelser i perioden, og skill mellom det som er dokumentert i dataene og mulige forklaringer.", label: "Siste 7 dager" },
  "30d": { tab: "30 dager", ask: "Forklar endringen i produksjonen for porteføljen siste 30 dager. Hent produksjon per døgn, vannføring og hendelser i perioden, og skill mellom det som er dokumentert i dataene og mulige forklaringer.", label: "Siste 30 dager" },
};

const LegendSwatch = ({ color, dashed }) => <span aria-hidden="true" className={cn("inline-block h-0 w-4 border-t-2", dashed ? "border-dashed" : "border-solid")} style={{ borderColor: color }} />;

/* one supporting figure: label, value, one line of context */
const Support = ({ label, value, unit, sub, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="text-[13.5px] text-muted-foreground">{label}</div>
    <div className="mt-1 flex items-baseline gap-1.5">
      <span className="metric text-[22px] font-semibold leading-none tracking-[-0.02em] text-foreground">{value}</span>
      {unit ? <span className="text-[14px] font-medium text-muted-foreground">{unit}</span> : null}
    </div>
    {sub ? <div className="mt-1.5 text-[13px] leading-snug text-muted-foreground">{sub}</div> : null}
  </div>
);

export default function OverviewPage() {
  const { isDark } = useTheme();
  const { openWith } = useAssistant();
  const { customer, isProxima } = useAuth();
  const [range, setRange] = useState("today");
  const { data, isLoading, isError, refetch } = useApi("/portfolio/summary", {}, { refetchInterval: 120_000 });
  const k = data?.kpis || {};
  const plants = useMemo(() => sortDeviationsFirst(data?.plants || []), [data]);
  const followUpCount = useMemo(() => (data?.plants || []).filter((p) => p.status !== "running" || (p.events_open || 0) > 0).length, [data]);

  if (isError) return <ErrorState message="Kunne ikke hente oversikten." onRetry={refetch} testId="overview-error" />;

  const r = RANGE[range];
  const producing = k.plants_running ?? 0; // running + reduced
  const asOf = data?.as_of;
  const title = customer?.short_name || customer?.name || (isProxima ? "Alle kunder" : "Oversikt");
  const yesterdayLine = isDark ? "hsl(206 18% 34%)" : "hsl(210 14% 76%)";
  const todayLine = isDark ? "hsl(203 88% 62%)" : "hsl(208 91% 36%)";

  /* the head figure follows the period: measured power today, energy for 7 or 30 days */
  const delta7 = (() => {
    const rows = data?.series_7d || [];
    const prev = data?.series_30d || [];
    if (!rows.length || prev.length < 14) return null;
    const last7 = rows.reduce((s, x) => s + (x.energy_mwh || 0), 0);
    const before = prev.slice(-14, -7).reduce((s, x) => s + (x.energy_mwh || 0), 0);
    return before ? ((last7 - before) / before) * 100 : null;
  })();
  const head =
    range === "today"
      ? { value: fmtNum(k.power_now_mw, 1), unit: "MW", sub: <><span className="metric">{fmtNum(k.utilisation_pct, 0)} %</span> av installert effekt · <span className="metric">{producing} av {k.plants_total}</span> produserer</> }
      : range === "7d"
        ? { value: fmtNum(k.energy_7d_mwh, 0), unit: "MWh", sub: <>Siste 7 dager{delta7 !== null ? <> · <Delta value={delta7} neutral className="text-[14px] text-foreground" /> mot uken før</> : null} · tilgjengelighet <span className="metric">{fmtNum(k.availability_7d_pct, 1)} %</span></> }
        : { value: fmtNum(k.energy_30d_mwh, 0), unit: "MWh", sub: <>Siste 30 dager{k.energy_30d_delta_pct !== null && k.energy_30d_delta_pct !== undefined ? <> · <Delta value={k.energy_30d_delta_pct} neutral className="text-[14px] text-foreground" /> mot forrige 30 dager</> : null} · tilgjengelighet <span className="metric">{fmtNum(k.availability_30d_pct, 1)} %</span></> };
  const fall = range === "today" ? (k.energy_today_delta_pct ?? 0) < -2 : range === "7d" ? (delta7 ?? 0) < -2 : (k.energy_30d_delta_pct ?? 0) < -2;
  const explainLabel = fall ? "Forklar produksjonsfallet" : "Forklar utviklingen";
  const explain = () => openWith(r.ask, { label: `Porteføljen · ${r.label}`, newConversation: true });

  return (
    <div className="space-y-10" data-testid="overview-page">
      {/* Page head: the customer is the page. The nav already says where you are. */}
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2" data-testid="overview-header">
        <div className="min-w-0">
          <h1 className="text-[28px] font-semibold leading-[1.15] tracking-[-0.015em]" data-testid="overview-title">
            {title}
          </h1>
          {isLoading ? <Skeleton className="mt-2 h-5 w-64" /> : (
            <p className="mt-1 text-[15px] text-muted-foreground" data-testid="overview-meta">
              <span className="metric">{k.plants_total}</span> kraftverk · <span className="metric">{fmtNum(k.capacity_mw, 1)}</span> MW installert
            </p>
          )}
        </div>
        {!isLoading ? (
          <p className="text-[13.5px] text-muted-foreground" data-testid="overview-asof">
            Målinger kl. <span className="metric">{fmtTime(asOf)}</span>
          </p>
        ) : null}
      </header>

      {/* Production (the main content) + what needs attention */}
      <section className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(280px,320px)] lg:gap-14">
        <div className="min-w-0" data-testid="oversikt-produksjonsgraf">
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div className="min-w-0">
              <div className="label-caps">Produksjon</div>
              {isLoading ? (
                <Skeleton className="mt-3 h-10 w-48" />
              ) : (
                <>
                  <div className="mt-2 flex items-baseline gap-2" data-testid="overview-head-figure">
                    <span className="metric text-[38px] font-semibold leading-none tracking-[-0.03em] text-foreground sm:text-[40px]">{head.value}</span>
                    <span className="text-[18px] font-medium text-muted-foreground">{head.unit}</span>
                  </div>
                  <div className="mt-2 text-[14.5px] leading-snug text-muted-foreground" data-testid="overview-head-sub">
                    {head.sub}
                  </div>
                </>
              )}
            </div>
            <Tabs value={range} onValueChange={setRange}>
              <TabsList className="segmented" data-testid="overview-trend-tabs">
                {Object.entries(RANGE).map(([key, v]) => (
                  <TabsTrigger key={key} value={key}>
                    {v.tab}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          <div className="mt-5">
            {isLoading ? (
              <Skeleton className="h-[320px] w-full" />
            ) : range === "today" ? (
              <TodayPowerChart today={data.series_today} yesterday={data.series_yesterday} capacity={k.capacity_mw} height={340} isDark={isDark} testId="overview-chart-today" />
            ) : (
              <DailyEnergyChart rows={range === "7d" ? data.series_7d : data.series_30d} height={340} isDark={isDark} testId={`overview-chart-${range}`} markLastIncomplete />
            )}
          </div>

          {!isLoading ? (
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-5 gap-y-1 text-[13px] text-muted-foreground" data-testid="overview-chart-note">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                {range === "today" ? (
                  <>
                    <span className="inline-flex items-center gap-2"><LegendSwatch color={todayLine} /> I dag</span>
                    <span className="inline-flex items-center gap-2"><LegendSwatch color={yesterdayLine} /> I går</span>
                    <span>Målt til kl. <span className="metric">{fmtTime(asOf)}</span> · dagen er ikke ferdig</span>
                  </>
                ) : (
                  <span>MWh per døgn · siste søyle er i dag og er ikke ferdig</span>
                )}
              </div>
              <Button variant="ghost" size="sm" className="-mr-2 h-8 gap-2 px-2 text-[13px] text-muted-foreground hover:text-foreground" onClick={explain} data-testid="overview-explain-button">
                <LensMark className="h-3.5 w-3.5" />
                {explainLabel}
              </Button>
            </div>
          ) : null}

          {/* supporting numbers – after the production, not before */}
          {!isLoading ? (
            <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-6 border-t border-border pt-5 sm:grid-cols-3" data-testid="overview-secondary-numbers">
              <Support label="Produsert i dag" value={fmtNum(k.energy_today_mwh, 0)} unit="MWh" sub={k.energy_today_delta_pct !== null && k.energy_today_delta_pct !== undefined ? <span className="inline-flex items-baseline gap-1.5"><Delta value={k.energy_today_delta_pct} neutral className="text-[13px] text-foreground" /> mot i går på samme tid</span> : <>I går <span className="metric">{fmtMWh(k.energy_yesterday_mwh)}</span></>} testId="secondary-energy-today" />
              <Support label="Est. inntekt i dag" value={fmtNOK(k.revenue_today_nok)} sub={<>Spotpris nå <span className="metric">{fmtPrice(k.spot_now_nok_mwh)}</span></>} testId="secondary-revenue-today" />
              <Support label="Tilgjengelighet, 30 d" value={fmtNum(k.availability_30d_pct, 1)} unit="%" sub={<><span className="metric">{fmtMWh(k.energy_30d_mwh)}</span> produsert</>} testId="secondary-availability" />
            </div>
          ) : null}
        </div>

        <aside className="min-w-0" data-testid="oversikt-krever-oppfolging">
          <div className="flex h-7 items-baseline justify-between gap-3">
            <h2 className="label-caps">
              Krever oppfølging{!isLoading && followUpCount ? <span className="ml-1.5 text-foreground">· {followUpCount}</span> : null}
            </h2>
            <Link to="/hendelser" className="text-[13px] font-medium text-primary hover:underline" data-testid="overview-all-events-link">
              Alle hendelser
            </Link>
          </div>
          <div className="mt-3">
            <FollowUp plants={data?.plants || []} events={data?.recent_events || []} loading={isLoading} asOf={asOf} />
          </div>
        </aside>
      </section>

      {/* Full plant table – the comparison surface */}
      <section data-testid="oversikt-kraftverk-liste">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="label-caps">
            Kraftverk{!isLoading ? <span className="ml-1.5 text-foreground">· {plants.length}</span> : null}
          </h2>
          <Link to="/kraftverk" className="text-[13px] font-medium text-primary hover:underline" data-testid="overview-all-plants-link">
            Filtre og kart
          </Link>
        </div>
        <PlantTable className="mt-3" plants={plants} loading={isLoading} emptyText="Ingen kraftverk i porteføljen. Kontakt administrator for tilgang." testId="overview-plant-table" />
      </section>
    </div>
  );
}
